import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import CircularSlider from 'react-circular-slider-svg';

interface DirectionDialProps {
  /** Position of the dial handle, 0–360. */
  angleDegrees: number;
  onChange: (angleDegrees: number) => void;
  size?: number;
  arcColor?: string;
}

/** Per-instance id source for the SVG arrowhead markers. */
let directionDialInstanceCount = 0;

/**
 * The circular "preferred direction" dial from Smart Path Settings: a
 * double-ended arrow on a 0–360° dial, making clear the angle is an
 * orientation of travel rather than a heading.
 *
 * Shared by the Smart Path form and the per-sample core-line rotation dialog so
 * both read the same way to the operator.
 */
const DirectionDial = ({ angleDegrees, onChange, size = 200, arcColor = '#aaaaaa' }: DirectionDialProps) => {
  // SVG marker ids must be unique per instance — the Smart Path form and the
  // rotation dialog can be mounted at once. React 17 has no useId, so the
  // instance gets a stable generated prefix instead.
  const markerIdPrefix = useMemo(() => `direction-dial-${++directionDialInstanceCount}`, []);
  const startMarkerId = `${markerIdPrefix}-arrowhead-start`;
  const endMarkerId = `${markerIdPrefix}-arrowhead-end`;

  return (
    <Box position="relative" width={size} height={size} sx={{ '& circle': { display: 'none !important' } }}>
      <CircularSlider
        size={size}
        minValue={0}
        maxValue={360}
        handle1={{
          value: angleDegrees,
          onChange: (v) => onChange(v),
        }}
        coerceToInt
        arcColor={arcColor}
        angleType={{ direction: 'ccw', axis: '+x' }}
      />

      {/* Overlay: Double-ended arrow */}
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ pointerEvents: 'none' }}
      >
        <svg
          width={80}
          height={50}
          viewBox="0 0 70 50"
          style={{
            transform: `rotate(${-angleDegrees}deg)`,
          }}
        >
          <defs>
            {/* Right-pointing arrowhead (end) */}
            <marker id={endMarkerId} markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" fill="#333" />
            </marker>

            {/* Left-pointing arrowhead (start) */}
            <marker id={startMarkerId} markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto-start-reverse">
              <path d="M0,0 L6,3 L0,6 Z" fill="#333" />
            </marker>
          </defs>

          {/* x1 / x2 → spread further for more length */}
          {/* strokeWidth → to make the shaft thicker */}
          <line
            x1="0"
            y1="25"
            x2="70"
            y2="25"
            stroke="#333"
            strokeWidth="2"
            markerStart={`url(#${startMarkerId})`}
            markerEnd={`url(#${endMarkerId})`}
          />
        </svg>
      </Box>
    </Box>
  );
};

export default DirectionDial;
